import {
  AddPhotoAlternateOutlined as AddPhotoAlternateOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import CollectionsSharpIcon from '@mui/icons-material/CollectionsSharp';
import MovieFilterSharpIcon from '@mui/icons-material/MovieFilterSharp';
import PhotoCameraSharpIcon from '@mui/icons-material/PhotoCameraSharp';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { forwardRef, memo, useEffect, useImperativeHandle, useState } from 'react';
import Image from 'src/components/image/Image';
import ImageDraggable from 'src/components/image/image-draggable';
import TextIconLabel from 'src/components/TextIconLabel';
import { NewPostStyledComponent } from 'src/pages/new-post-new/newPostStyled.component';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import { CommonHelper } from 'src/utils/commonHelper';
import { NewPostErrorUtil } from '../new-post-errors-util';
import PostDescription from '../post-description.component';
import PostFirstComment from '../post-first-comment.component';
import { PostUtil } from '../post-util';
import TriggerConfigDialog from './TriggerConfigDialog';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommonThumbnail from './common-thumbnail';

const FacebookConditionComponent = forwardRef(({ updateRequestData,isOrgConnectedProfilesData }, ref) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();

  // State Variables
  const [socialMediaRequestData, setSocialMediaRequestData] = useState([]);
  const [requestData, setRequestData] = useState([]);
  const [manualExpand, setManualExpand] = useState({});
  const [openFacebookThumbnailImage, setOpenFacebookThumbnailImage] = useState({});
  const [openTriggerDialog, setOpenTriggerDialog] = useState({});
  const [isConfiguring, setIsConfiguring] = useState({});

  useEffect(() => {
    checkForErrors();
  }, []);

  useImperativeHandle(ref, () => ({
    checkForErrors,
    handleRequestData,
  }));

  useEffect(() => {
    Object.keys(requestData).forEach((profileId) => {
      let hasErrors = requestData[profileId].some((profile) => profile.errors && profile.errors.length > 0);

      if (!hasErrors && manualExpand[profileId] === undefined) {
        setManualExpand((prev) => ({
          ...prev,
          [profileId]: true,
        }));
      }
    });
  }, [requestData]);

  const handleRequestData = (data) => {
    checkForErrors(data, false);
  };

  const updateRequestDataToParent = (data) => {
    updateRequestData(data, PostUtil.ServiceName.FACEBOOK);
  };

  const handleChangeSocialMediaRequestData = (data, isUpdateDataToParent = true) => {
    let formattedRequestData = CommonHelper.B2SGroupBy(data, 'connected_profile_id');
    setRequestData({ ...formattedRequestData });
    setSocialMediaRequestData([...data]);
    if (isUpdateDataToParent === true) {
      updateRequestDataToParent(data);
    }
  };

  const checkForErrors = (data = null, isUpdateDataToParent = true) => {
    let sRequestData = data ? [...data] : [...socialMediaRequestData];
    if (sRequestData?.length > 0) {
      sRequestData.map((request) => {
        let errors = getFacebookErrors(request);
        request['errors'] = errors;
      });
      handleChangeSocialMediaRequestData(sRequestData, isUpdateDataToParent);
    } else {
      handleChangeSocialMediaRequestData([], []);
    }
  };

  const handleReOrderFiles = (profileId, postType, oldIndex, newIndex) => {
    let sRequestData = [...socialMediaRequestData];
    let reqData = sRequestData.find((a) => a.connected_profile_id === parseInt(profileId) && a.post_type === postType);
    let files = [...reqData.media_data];
    let mediaFiles = CommonHelper.ReArrangeArray(files, oldIndex, newIndex);
    reqData.media_url = mediaFiles.map((a) => a.media_url).join();
    reqData.media_data = mediaFiles;
    checkForErrors(sRequestData);
  };

  const handleRemoveFiles = (profileId, postType, mediaUrl) => {
    let sRequestData = [...socialMediaRequestData];
    let rData = sRequestData.find((a) => a.connected_profile_id === parseInt(profileId) && a.post_type === postType);
    let files = [...rData.media_data];
    let index = files.findIndex((a) => a.media_url === mediaUrl);
    if (index >= 0) files.splice(index, 1);
    rData.media_url = files.map((a) => a.media_url).join();
    rData.media_data = files;
    checkForErrors(sRequestData);
  };

  const handleChangeDescription = (profileId, profile, value) => {
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    connectedProfile.description = value;
    checkForErrors(sRequestData);
  };

  const handleLink = (profileId, profile, value) => {
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    connectedProfile.link = value;
    checkForErrors(sRequestData);
  };

  const handleChangeThumbNailImage = (profileId, profile, media_url) => {
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    connectedProfile.thumbnail = media_url;
    checkForErrors(sRequestData);
  };

  const handleEnableAutoDM = (profileId, profile) => {
    const key = `${profileId}_${profile.post_type}`;
    setIsConfiguring((prev) => ({ ...prev, [key]: true }));

    if (profile.automation_trigger?.trigger_type) {
      let sRequestData = [...socialMediaRequestData];
      let connectedProfile = sRequestData.find(
        (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
      );
      connectedProfile.is_automation_trigger = true;
      checkForErrors(sRequestData);
      setIsConfiguring((prev) => ({ ...prev, [key]: false }));
    } else {
      handleOpenTriggerDialog(profileId, profile.post_type);
    }
  };

  const handleDisableAutoDM = (profileId, profile) => {
    const key = `${profileId}_${profile.post_type}`;
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    connectedProfile.is_automation_trigger = false;
    checkForErrors(sRequestData);
    setIsConfiguring((prev) => ({ ...prev, [key]: false }));
  };

  const handleChangeFirstComment = (profileId, profile, value) => {
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    connectedProfile.comment_description = value;
    connectedProfile.has_comment = connectedProfile.comment_description === '' ? false : true;
    checkForErrors(sRequestData);
  };

  const handlePostChange = (profileId, postType) => {
    let sRequestData = [...socialMediaRequestData];
    let profileCount = sRequestData.filter((a) => a.connected_profile_id === parseInt(profileId))?.length;
    let index = sRequestData.findIndex(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === postType
    );
    if (index >= 0) {
      if (profileCount > 1) {
        sRequestData.splice(index, 1);
      }
    } else {
      let sNetwork = { ...sRequestData.find((a) => a.connected_profile_id === parseInt(profileId)) };
      sNetwork['post_type'] = postType;
      sNetwork['errors'] = getFacebookErrors(sNetwork);
      sRequestData.push(sNetwork);
    }
    checkForErrors(sRequestData);
  };

  const facebookReelsConditions = (request) => {
    return NewPostErrorUtil.FacebookReelsConditions(request,translate);
  };

  const facebookStoryConditions = (request) => {
    return NewPostErrorUtil.FacebookStoryConditions(request,translate);
  };

  const facebookPostConditions = (request) => {
    return NewPostErrorUtil.FacebookPostConditions(request,translate);
  };

  const getFacebookErrors = (request) => {
    let fErrors = [];
    let postType = request.post_type;

    if (postType === PostUtil.PostType.POST) {
      let postErrors = facebookPostConditions(request,translate);
      fErrors = fErrors.concat(postErrors);
    } else if (postType === PostUtil.PostType.REELS) {
      let reelsErrors = facebookReelsConditions(request,translate);
      fErrors = fErrors.concat(reelsErrors);
    } else if (postType === PostUtil.PostType.STORY) {
      let storyErrors = facebookStoryConditions(request,translate);
      fErrors = fErrors.concat(storyErrors);
    }
    return fErrors;
  };

  const handleAccordion = (profileId, hasErrors) => {
    if (hasErrors) return;

    setManualExpand((prev) => ({
      ...prev,
      [profileId]: !prev[profileId],
    }));
    let sRequestData = [...socialMediaRequestData];
    let reqDatas = sRequestData.filter((a) => a.connected_profile_id === parseInt(profileId));
    if (reqDatas?.length > 0) {
      reqDatas.map((data) => {
        data['isExpanded'] = !data['isExpanded'];
      });
    }
    handleChangeSocialMediaRequestData(sRequestData, false);
  };

  const checkAspectRatioChanges = () => {
    // Placeholder for aspect ratio logic
    return false;
  };

  const handleChangeAspectRatio = () => {};

  const handleThumbnailImagePopup = (profileId, postType, show = false) => {
    setOpenFacebookThumbnailImage((prev) => ({
      ...prev,
      [`${profileId}_${postType}`]: show,
    }));
  };

  const handleOpenTriggerDialog = (profileId, postType) => {
    setOpenTriggerDialog((prev) => ({
      ...prev,
      [`${profileId}_${postType}`]: true,
    }));
  };

  const handleCloseTriggerDialog = (profileId, postType) => {
    setOpenTriggerDialog((prev) => ({
      ...prev,
      [`${profileId}_${postType}`]: false,
    }));
    setIsConfiguring((prev) => ({ ...prev, [`${profileId}_${postType}`]: false }));
  };

  const handleSaveTrigger = (profileId, profile, trigger) => {
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    connectedProfile.is_automation_trigger = true;
    connectedProfile.automation_trigger = {
      trigger_type: trigger.trigger_type || 'like',
      trigger_values: trigger.any_trigger_value ? [] : trigger.trigger_values || [],
      any_trigger_value: trigger.any_trigger_value || false,
      dm_message: trigger.dm_message || '',
      comment_reply: trigger.comment_reply || '',
      dm_template: trigger.is_dm_template ? trigger.dm_template : null,
      is_dm_template: trigger.is_dm_template || false,
    };
    checkForErrors(sRequestData);
    handleCloseTriggerDialog(profileId, profile.post_type);
    const key = `${profileId}_${profile.post_type}`;
    setIsConfiguring((prev) => ({ ...prev, [key]: false }));
  };

  const checkPostTypeChecked = (profileId, postType) => {
    return requestData[profileId].some((a) => a.post_type === postType);
  };

  const errorView = (request) => {
    return (
      <Box style={{ marginTop: '0.5rem' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{translate('facebook_condition.error_description', 'Error Description')}</Typography>
        {request.errors.map((errorMessage) => (
          <TextIconLabel
            key={errorMessage}
            icon={<ErrorOutlineIcon sx={{ color: theme.palette.error.main, mr: 1, fontSize: 18 }} />}
            value={errorMessage}
            sx={{ typography: 'caption', color: theme.palette.error.main, mb: 1, fontWeight: 800, fontSize: 14 }}
          />
        ))}
      </Box>
    );
  };

  const POST_TYPES = {
    post: {
      value: 'post',
      icon: <CollectionsSharpIcon sx={{ width: 17, height: 17, mr: 0.5 }} />,
    },
    reels: {
      value: 'reels',
      icon: <MovieFilterSharpIcon sx={{ width: 17, height: 17, mr: 0.5 }} />,
    },
    story: {
      value: 'story',
      icon: <PhotoCameraSharpIcon sx={{ width: 17, height: 17, mr: 0.5 }} />,
    },
  };

  return (
    <>
      {requestData &&
        Object.keys(requestData)?.length > 0 &&
        Object.keys(requestData).map((profileId) => {
          let isExpanded = socialMediaRequestData.find(
            (a) => a.connected_profile_id === parseInt(profileId)
          )?.isExpanded;

          let hasErrors = requestData[profileId].some((profile) => profile.errors && profile.errors.length > 0);
          let borderColor = hasErrors ? theme.palette.error.main : theme.palette.text.border;
          let connectedProfileLength = requestData[profileId].some(
            (profile) =>
              profile?.connected_profile_details?.connected_profile_name &&
              profile?.connected_profile_details?.connected_profile_name.length > 10
          );
          let paddingTop = connectedProfileLength ? 0.5 : 0.5;
          return (
            <Accordion
              key={profileId}
              expanded={hasErrors || manualExpand[profileId] || false}
              onChange={() => handleAccordion(profileId, hasErrors)}
              style={{ ...NewPostStyledComponent.AccordionStyle, borderColor }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  ...NewPostStyledComponent.AccordionSummary,
                  paddingTop,
                }}
              >
                <Grid container justifyContent="space-between">
                  <Grid alignItems="center" display="flex">
                    {isOrgConnectedProfilesData ?  CommonFunction.SocialMediaBadgeWithName(
                      requestData[profileId].find((profile) => profile.connected_profile_id.toString() === profileId)
                        ?.connected_profile_details
                    ): CommonFunction.AccountBadgeWithName(
                      requestData[profileId].find((profile) => profile.connected_profile_id.toString() === profileId)
                        ?.connected_profile_details
                    )}
                  </Grid>
                  <Grid
                    item
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <FormGroup style={{ display: 'inline' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkPostTypeChecked(profileId, PostUtil.PostType.POST)}
                            onChange={() => handlePostChange(profileId, PostUtil.PostType.POST)}
                          />
                        }
                        label={
                          <NewPostStyledComponent.AccordianLabelStyles>
                            {PostUtil.PostType.POST}
                          </NewPostStyledComponent.AccordianLabelStyles>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkPostTypeChecked(profileId, PostUtil.PostType.REELS)}
                            onChange={() => handlePostChange(profileId, PostUtil.PostType.REELS)}
                          />
                        }
                        label={
                          <NewPostStyledComponent.AccordianLabelStyles>
                            {PostUtil.PostType.REELS}
                          </NewPostStyledComponent.AccordianLabelStyles>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkPostTypeChecked(profileId, PostUtil.PostType.STORY)}
                            onChange={() => handlePostChange(profileId, PostUtil.PostType.STORY)}
                          />
                        }
                        label={
                          <NewPostStyledComponent.AccordianLabelStyles>
                            {PostUtil.PostType.STORY}
                          </NewPostStyledComponent.AccordianLabelStyles>
                        }
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
                <Divider />
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0.5 }}>
                {requestData[profileId].map((profile) => {
                  const key = `${profileId}_${profile.post_type}`;
                  return (
                    <Box
                      key={key}
                      style={{
                        ...NewPostStyledComponent.AccordionStyle,
                        borderColor:
                          profile.errors && profile.errors.length > 0
                            ? theme.palette.error.main
                            : theme.palette.background.paper,
                        padding: '0.5rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {profile.post_type === POST_TYPES[profile.post_type].value && (
                        <Box
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            color: theme.palette.primary.main,
                            marginTop: 4,
                            fontSize: '0.875rem',
                            fontWeight: 800,
                          }}
                        >
                          {POST_TYPES[profile.post_type].icon}
                          {POST_TYPES[profile.post_type].value.toUpperCase()}
                        </Box>
                      )}
                      <PostDescription
                        handleChangeDescription={(event) =>
                          handleChangeDescription(profileId, profile, event)
                        }
                        valueDescription={profile.description}
                      />
                      {profile.post_type !== PostUtil.PostType.REELS &&
                        profile.post_type !== PostUtil.PostType.STORY && (
                          <>
                            <Typography
                              style={{
                                color: theme.palette.text.secondary,
                                ...theme.typography.subtitle2,
                                marginBottom: '8px',
                              }}
                            >
                              {translate('facebook_condition.link', 'Link')}
                            </Typography>
                            <TextField
                              name="link"
                              placeholder={translate('facebook_condition.enter_link_optional', 'Enter Link (Optional)')}
                              fullWidth
                              onChange={(event) => handleLink(profileId, profile, event.target.value)}
                              value={profile.link}
                              style={{ position: 'relative', marginBottom: '8px' }}
                            />
                          </>
                        )}
                      {profile.media_data?.length > 0 && (
                        <Box style={{ marginTop: 3 }}>
                          <ImageDraggable
                            mediaFiles={profile.media_data}
                            handleRemoveFiles={(e) => handleRemoveFiles(profileId, profile.post_type, e)}
                            handleReOrderFiles={(e, k) =>
                              handleReOrderFiles(profileId, profile.post_type, e, k)
                            }
                          />
                        </Box>
                      )}
                      {(profile.post_type === PostUtil.PostType.REELS ||
                        profile.post_type === PostUtil.PostType.POST) &&
                        profile?.media_data?.length > 0 &&
                        profile.media_data[0]?.media_type === 'VIDEO' && (
                          <>
                            <Typography
                              style={{
                                color: theme.palette.text.secondary,
                                ...theme.typography.subtitle2,
                              }}
                            >
                              Thumbnail Image
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexWrap: 'wrap',
                              }}
                            >
                              {profile?.thumbnail && profile.media_data?.length > 0 && (
                                <Box
                                  sx={{
                                    p: 0,
                                    m: 0.5,
                                    width: 80,
                                    height: 80,
                                    borderRadius: 1.25,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    display: 'inline-flex',
                                    opacity: 1,
                                    border: (theme) => `solid 1px ${theme.palette.divider}`,
                                  }}
                                >
                                  <Image src={profile.thumbnail} alt={profile.thumbnail} ratio="1/1" />
                                </Box>
                              )}
                              {profile.media_data?.length > 0 && (
                                <Box
                                  sx={{
                                    p: 0,
                                    m: 0.5,
                                    width: 80,
                                    height: 80,
                                    borderRadius: 1.25,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    display: 'inline-flex',
                                    opacity: 1,
                                    border: (theme) => `solid 1px ${theme.palette.divider}`,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleThumbnailImagePopup(profileId, profile.post_type, true)}
                                >
                                  <AddPhotoAlternateOutlinedIcon />
                                  <Typography variant="caption" sx={{ textAlign: 'center' }}>
                                    {profile?.thumbnail ? translate('facebook_condition.update_thumbnail', 'Update Thumbnail') : translate('facebook_condition.upload_thumbnail', 'Upload Thumbnail')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </>
                        )}
                      {openFacebookThumbnailImage[`${profileId}_${profile.post_type}`] &&
                        profile.media_data?.length > 0 && (
                          <CommonThumbnail  
                          videoSrc={profile.media_data[0]}
                           serviceName="facebook"
                            handleClose={() => handleThumbnailImagePopup(profileId, profile.post_type, false)}
                            handleChangeThumbnail={(coverImageUrl) =>
                              handleChangeThumbNailImage(profileId, profile, coverImageUrl)
                            }/>
                        )}
                      <PostFirstComment
                        handleChangeDescription={(event) =>
                          handleChangeFirstComment(profileId, profile, event)
                        }
                        valueDescription={profile.comment_description}
                      />
                   {!isOrgConnectedProfilesData && profile.post_type !== PostUtil.PostType.STORY && (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {translate('facebook_condition.auto_dm_configuration', 'Auto DM Configuration')}
                          </Typography>
                          {profile?.connected_profile_details?.webhook_profile_id === -1 ? (
                            <Box
                              sx={{
                                border: `1px solid ${theme.palette.warning.main}20`,
                                borderRadius: 1,
                                p: 2,
                                backgroundColor: `${theme.palette.warning.main}08`,
                              }}
                            >
                              <TextIconLabel
                                icon={<ErrorOutlineIcon color="warning" sx={{ mr: 1 }} />}
                                value={translate('facebook_condition.webhook_required', 'Webhook configuration is required to enable Auto DMs')}
                                sx={{ fontWeight: 'bold', color: theme.palette.warning.dark }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 1,
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                {translate('facebook_condition.connect_webhook_to_use', 'Please connect a webhook to use this feature.')}
                              </Typography>
                              <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                sx={{ mt: 1.5 }}
                                onClick={() => navigate('/crm', { state: { data: 'crm-accounts' } })}
                                startIcon={<SettingsIcon fontSize="small" />}
                              >
                                {translate('facebook_condition.connect_webhook', 'Connect Webhook')}
                              </Button>
                            </Box>
                          ) : profile.is_automation_trigger ? (
                            <Box
                              sx={{
                                border: `1px solid ${theme.palette.success.main}20`,
                                borderRadius: 1,
                                p: 2,
                                backgroundColor: `${theme.palette.success.main}08`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Box>
                                  <TextIconLabel
                                    icon={<CheckCircleIcon color="success" sx={{ mr: 1 }} />}
                                    value={translate('facebook_condition.auto_dm_enabled', 'Auto DMs are enabled')}
                                    sx={{ fontWeight: 'bold', color: theme.palette.success.dark }}
                                  />
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                    {translate('facebook_condition.trigger', 'Trigger')}: {profile.automation_trigger.trigger_type}
                                  </Typography>
                                  {profile.automation_trigger.any_trigger_value ? (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                      {translate('facebook_condition.trigger_values', 'Trigger Values')}: <Chip label={translate('facebook_condition.any', 'Any')} size="small" color="primary" variant="outlined" />
                                    </Typography>
                                  ) : profile.automation_trigger.trigger_values?.length > 0 ? (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                      {translate('facebook_condition.trigger_values', 'Trigger Values')}: {' '}
                                      <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {profile.automation_trigger.trigger_values.map((value, index) => (
                                          <Chip
                                            key={index}
                                            label={value}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                          />
                                        ))}
                                      </Box>
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleOpenTriggerDialog(profileId, profile.post_type)}
                                  startIcon={<SettingsIcon fontSize="small" />}
                                >
                                  {translate('facebook_condition.edit', 'Edit')}
                                </Button>
                              </Box>
                              <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                size="small"
                                sx={{ mt: 1.5 }}
                                onClick={() => handleDisableAutoDM(profileId, profile)}
                              >
                                {translate('facebook_condition.disable_auto_dms', 'Disable Auto DMs')}
                              </Button>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                p: 2,
                              }}
                            >
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={() => handleEnableAutoDM(profileId, profile)}
                                disabled={isConfiguring[`${profileId}_${profile.post_type}`]}
                                startIcon={
                                  isConfiguring[`${profileId}_${profile.post_type}`] ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <SendIcon fontSize="small" />
                                  )
                                }
                              >
                                {isConfiguring[`${profileId}_${profile.post_type}`]
                                  ? translate('facebook_condition.configuring', 'Configuring...')
                                  : translate('facebook_condition.enable_auto_dms', 'Enable Auto DMs')}
                              </Button>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 1,
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                {translate('facebook_condition.auto_dm_caption', 'Automatically send DMs when users interact with this post')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      {!isOrgConnectedProfilesData && openTriggerDialog[`${profileId}_${profile.post_type}`] && (
                        <TriggerConfigDialog
                          open={true}
                          onClose={() => handleCloseTriggerDialog(profileId, profile.post_type)}
                          onSave={(trigger) => handleSaveTrigger(profileId, profile, trigger)}
                          profile={profile}
                          type={profile.automation_trigger?.trigger_type || 'comment'}
                        />
                      )}
                      {checkAspectRatioChanges() ? (
                        <AccordionDetails sx={{ padding: 2 }}>
                          <Typography sx={{ fontSize: 16, fontWeight: 800, mt: 2 }}>{translate('facebook_condition.additional_information', 'Additional Information')}</Typography>
                          <Grid container sx={{ mt: 2, mb: 3, display: 'flex', alignItems: 'center' }} xs={12}>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={profile.changeFacebookAspectRatio}
                                    onChange={(event) => handleChangeAspectRatio(PostUtil.ServiceName.FACEBOOK)}
                                  />
                                }
                                label={translate('facebook_condition.change_aspect_ratio', 'Change Aspect Ratio for video')}
                              />
                            </FormGroup>
                          </Grid>
                          {profile.errors && profile.errors.length > 0 && errorView(profile)}
                        </AccordionDetails>
                      ) : (
                        profile.errors && profile.errors.length > 0 && errorView(profile)
                      )}
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
    </>
  );
});

export default memo(FacebookConditionComponent);